import styled from '@emotion/styled';
import type { EngagementCategory } from 'api/rest/engagement_score';

type CategoryTone = {
    /** Solid color used for the score number and accent dot. */
    accent: string;
    /** Background tint used for the status badge / score chip. */
    bg: string;
    /** Border color used for the status badge / score chip. */
    border: string;
    /** Text color used for the status badge. */
    text: string;
};

/**
 * Visual tone per engagement category. Kept conservative so the card scans
 * cleanly next to the (purple) Quick Actions surface above it.
 *
 * - HIGHLY_ENGAGED → green
 * - ENGAGED        → teal / blue-green
 * - MODERATE       → amber / neutral
 * - LOW_ENGAGEMENT → orange
 * - AT_RISK        → red
 */
export const CATEGORY_TONES: Record<EngagementCategory, CategoryTone> = {
    HIGHLY_ENGAGED: {
        accent: '#15803d',
        bg: 'rgba(34, 197, 94, 0.12)',
        border: 'rgba(34, 197, 94, 0.32)',
        text: '#15803d',
    },
    ENGAGED: {
        accent: '#0d9488',
        bg: 'rgba(20, 184, 166, 0.12)',
        border: 'rgba(20, 184, 166, 0.32)',
        text: '#0f766e',
    },
    MODERATE: {
        accent: '#b45309',
        bg: 'rgba(234, 179, 8, 0.14)',
        border: 'rgba(234, 179, 8, 0.36)',
        text: '#92400e',
    },
    LOW_ENGAGEMENT: {
        accent: '#c2410c',
        bg: 'rgba(249, 115, 22, 0.12)',
        border: 'rgba(249, 115, 22, 0.32)',
        text: '#9a3412',
    },
    AT_RISK: {
        accent: '#b91c1c',
        bg: 'rgba(239, 68, 68, 0.12)',
        border: 'rgba(239, 68, 68, 0.32)',
        text: '#991b1b',
    },
};

const FALLBACK_TONE: CategoryTone = {
    accent: '#475569',
    bg: 'rgba(148, 163, 184, 0.16)',
    border: 'rgba(148, 163, 184, 0.32)',
    text: '#334155',
};

export const getCategoryTone = (category?: string): CategoryTone => {
    if (!category) return FALLBACK_TONE;
    return CATEGORY_TONES[category as EngagementCategory] ?? FALLBACK_TONE;
};

/* -----------------------------------------------------------------------------
 * Compact card — sits below QuickActions. Clickable: opens the details modal.
 * Non-interactive variant (`as="div"`) is used for loading / error / empty
 * states so the user doesn't tap into an unhelpful modal.
 * ---------------------------------------------------------------------------*/

export const Card = styled.button`
    box-sizing: border-box;
    appearance: none;
    text-align: left;
    font-family: inherit;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 14px 16px;
    border: 1px solid rgba(131, 83, 226, 0.1);
    border-radius: 20px;
    background:
        radial-gradient(circle at top right, rgba(131, 83, 226, 0.08), transparent 34%),
        linear-gradient(180deg, #ffffff 0%, #fcfbff 100%);
    box-shadow:
        0 10px 24px -14px rgba(15, 23, 42, 0.18),
        0 1px 3px rgba(15, 23, 42, 0.04);
    width: 100%;
    position: relative;
    z-index: 1;
    transition:
        transform 0.2s ease,
        box-shadow 0.2s ease,
        border-color 0.2s ease;

    &:hover {
        transform: translateY(-1px);
        border-color: rgba(131, 83, 226, 0.28);
        box-shadow:
            0 14px 28px -16px rgba(15, 23, 42, 0.2),
            0 1px 3px rgba(15, 23, 42, 0.05);
    }

    &:focus-visible {
        outline: none;
        border-color: rgba(131, 83, 226, 0.45);
        box-shadow:
            0 0 0 3px rgba(131, 83, 226, 0.18),
            0 10px 24px -14px rgba(15, 23, 42, 0.18);
    }

    &:disabled {
        cursor: default;
        transform: none;
        box-shadow:
            0 10px 24px -14px rgba(15, 23, 42, 0.18),
            0 1px 3px rgba(15, 23, 42, 0.04);
    }

    @media (max-width: 768px) {
        margin: 0 16px;
        padding: 16px;
        border-radius: 20px;
        box-shadow:
            0 14px 32px -18px rgba(15, 23, 42, 0.2),
            0 1px 3px rgba(15, 23, 42, 0.04);
    }

    @media (max-width: 480px) {
        margin: 0 12px;
        padding: 14px;
    }
`;

/** Same surface as `Card` but never interactive — used for skeleton/error states. */
export const CardStatic = Card.withComponent('div');

export const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

export const Eyebrow = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 4px 8px;
    border-radius: 999px;
    background: rgba(131, 83, 226, 0.1);
    color: #6d41d8;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
`;

export const Chevron = styled.span`
    display: inline-flex;
    align-items: center;
    color: #94a3b8;

    svg {
        width: 14px;
        height: 14px;
    }
`;

export const MainRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

export const ScoreBlock = styled.div<{ accent: string }>`
    display: flex;
    align-items: baseline;
    gap: 4px;
    color: ${({ accent }) => accent};
    font-weight: 700;
    line-height: 1;
`;

export const ScoreNumber = styled.span`
    font-size: 32px;
    letter-spacing: -0.02em;
`;

export const ScoreTotal = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #94a3b8;
`;

export const CategoryBadge = styled.span<{ bg: string; border: string; text: string }>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 999px;
    background: ${({ bg }) => bg};
    border: 1px solid ${({ border }) => border};
    color: ${({ text }) => text};
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    white-space: nowrap;
`;

export const EmptyHint = styled.p`
    margin: 0;
    color: #94a3b8;
    font-size: 13px;
    font-style: italic;
    line-height: 1.4;
`;

export const ErrorBanner = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 12px;
    background: rgba(239, 68, 68, 0.08);
    border: 1px solid rgba(239, 68, 68, 0.24);
    color: #991b1b;
    font-size: 12.5px;
    line-height: 1.4;
`;

/* -----------------------------------------------------------------------------
 * Skeleton shimmer used while the score is loading.
 * ---------------------------------------------------------------------------*/

export const SkeletonBlock = styled.div<{ width?: string; height?: string; radius?: string }>`
    width: ${({ width }) => width ?? '100%'};
    height: ${({ height }) => height ?? '14px'};
    border-radius: ${({ radius }) => radius ?? '6px'};
    background: linear-gradient(
        90deg,
        rgba(148, 163, 184, 0.12) 0%,
        rgba(148, 163, 184, 0.22) 50%,
        rgba(148, 163, 184, 0.12) 100%
    );
    background-size: 200% 100%;
    animation: engagement-shimmer 1.4s ease-in-out infinite;

    @keyframes engagement-shimmer {
        0% {
            background-position: 200% 0;
        }
        100% {
            background-position: -200% 0;
        }
    }
`;

export const SkeletonRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

/* -----------------------------------------------------------------------------
 * Modal content — laid out inside the existing common <Modal> shell.
 * ---------------------------------------------------------------------------*/

export const ModalScoreRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
    padding: 14px 16px;
    border-radius: 16px;
    border: 1px solid rgba(148, 163, 184, 0.22);
    background: rgba(255, 255, 255, 0.72);
`;

export const ModalScoreNumber = styled.span<{ accent: string }>`
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    color: ${({ accent }) => accent};
    font-size: 40px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;

    span:last-child {
        font-size: 16px;
        font-weight: 600;
        color: #94a3b8;
    }
`;

export const SectionTitle = styled.span`
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #6b7280;
`;

export const SummaryText = styled.p`
    margin: 0;
    color: #374151;
    font-size: 14px;
    line-height: 1.5;
`;

export const ActionsList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const ActionItem = styled.li`
    position: relative;
    padding: 8px 12px 8px 24px;
    border-radius: 12px;
    background: rgba(131, 83, 226, 0.06);
    border: 1px solid rgba(131, 83, 226, 0.12);
    color: #1f2937;
    font-size: 13.5px;
    line-height: 1.45;

    &::before {
        content: '';
        position: absolute;
        left: 12px;
        top: 14px;
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: #6d41d8;
    }
`;

export const BreakdownList = styled.ul`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

export const BreakdownItem = styled.li`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 12px;
    background: rgba(248, 250, 252, 0.7);
    border: 1px solid rgba(148, 163, 184, 0.18);
    color: #1f2937;
    font-size: 13px;
    line-height: 1.35;
`;

export const BreakdownRule = styled.span`
    flex: 1 1 auto;
    min-width: 0;
    color: #374151;
`;

/** Sign-aware value chip — subtle green for positive, subtle red for negative. */
export const BreakdownValue = styled.span<{ tone: 'positive' | 'negative' | 'neutral' }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    ${({ tone }) => {
        if (tone === 'positive') {
            return `
                background: rgba(34, 197, 94, 0.12);
                color: #15803d;
                border: 1px solid rgba(34, 197, 94, 0.28);
            `;
        }
        if (tone === 'negative') {
            return `
                background: rgba(239, 68, 68, 0.10);
                color: #b91c1c;
                border: 1px solid rgba(239, 68, 68, 0.28);
            `;
        }
        return `
            background: rgba(148, 163, 184, 0.14);
            color: #475569;
            border: 1px solid rgba(148, 163, 184, 0.28);
        `;
    }}
`;

export const ModalSubsection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
